import {states, type State} from 'shared/constants';
import type {Identified, Pageable, Valuable} from 'src/typings';
import type {Observable, Subject} from 'rxjs';
import {BehaviorSubject} from 'rxjs';
import {findFirst, replaceAll, range, findFirstIndex, byIdOf} from 'shared/utils/array-utils';
import {assertExists, exists, isNullOrUndefined} from 'shared/utils/custom-utils';
import {distinctUntilChanged} from 'rxjs/operators';
import {isEqual} from 'lodash-es';

export type LoadListItemsFunction<ITEM extends Identified<ID>, ID, PARAMS extends Record<string, any>> = (pageable: Pageable, params: PARAMS) => Promise<ITEM[]>;
export type LoadSingleItemFunction<ITEM extends Identified<ID>, ID> = (id: ID) => Promise<ITEM>;
export type LoadListSizeFunction<PARAMS extends Record<string, any>> = (params: PARAMS) => Promise<Valuable<number>>;

export interface PageableListState<ITEM extends Identified<ID>, ID, PARAMS extends Record<string, any>> {
    readonly isPartiallyLoaded: boolean;
    readonly isAllLoaded: boolean
    readonly loadedSize: number | null;
    readonly totalSize: number | null;
    readonly currentState: State;
    readonly nextPage: Readonly<Pageable>;
    readonly loadedItems: ITEM[] | null;
    readonly params: PARAMS;
    readonly errorMessage: string | null;
}

export interface PageableList<ITEM extends Identified<ID>, ID, PARAMS extends Record<string, any>> extends PageableListState<ITEM, ID, PARAMS> {
    getState$(): Observable<PageableListState<ITEM, ID, PARAMS>>

    reset(): void;

    loadFirstPage(): Promise<ITEM[]>;
    loadNextPage(): Promise<ITEM[]>;
    loadTotalSize(): Promise<number>;

    replaceItem(item: ITEM): void;

    reloadAllLoaded(): Promise<ITEM[]>;
    resetAndReloadFirstPage(): Promise<ITEM[]>;
    reloadPage(pageNumber: number): Promise<ITEM[]>;
    reloadItem(item: ITEM): Promise<ITEM>;

    removeItem(item: ITEM): Promise<void>;
    removeItemById(id: ID): Promise<void>;
    removeAll(): void;
}

const DEFAULT_PAGE_SIZE = 20;
const FIRST_PAGE = 1;

export interface PageableListSettings<ITEM extends Identified<ID>, ID, PARAMS extends Record<string, any>> {
    loadListItems: LoadListItemsFunction<ITEM, ID, PARAMS>;
    loadListSize: LoadListSizeFunction<PARAMS>;
    initialParams: PARAMS;
    pageSize?: number | undefined;
    loadSingleItemFunction?: LoadSingleItemFunction<ITEM, ID> | undefined;
}


export abstract class GenericReactivePageableList<ITEM extends Identified<ID>, ID, PARAMS extends Record<string, any>> implements PageableList<ITEM, ID, PARAMS> {

    protected readonly loadListItems: LoadListItemsFunction<ITEM, ID, PARAMS>;
    protected readonly loadListSize: LoadListSizeFunction<PARAMS>;
    protected readonly initialParams: PARAMS;
    protected readonly pageSize: number;
    protected readonly loadSingleItemFunction?: LoadSingleItemFunction<ITEM, ID> | null;

    private readonly reactiveState$: Subject<PageableListState<ITEM, ID, PARAMS>>;

    #nextPage: Pageable;
    protected items: ITEM[] | null;
    protected size: number | null;
    protected state: State;
    protected currentParams: PARAMS;
    protected loadNextPageErrorMessage: string | null;

    protected constructor({loadListItems, loadListSize, initialParams, pageSize = DEFAULT_PAGE_SIZE, loadSingleItemFunction = null}: PageableListSettings<ITEM, ID, PARAMS>) {
        this.loadListItems = loadListItems;
        this.loadListSize = loadListSize;
        this.initialParams = initialParams;
        this.pageSize = pageSize;
        this.loadSingleItemFunction = loadSingleItemFunction;
        this.init();
        this.reactiveState$ = new BehaviorSubject(this.createState());
    }

    public getState$(): Observable<PageableListState<ITEM, ID, PARAMS>> {
        return this.reactiveState$.pipe(distinctUntilChanged(isEqual));
    }

    public reset(): void {
        this.init()
        this.emitCurrentData();
    }

    public async loadFirstPage(): Promise<ITEM[]> {
        if (this.isPartiallyLoaded) {
            return this.items;
        }
        this.items = await this.fetchNextPage();
        if (isNullOrUndefined(this.totalSize)) {
            await this.loadTotalSize(); // emits
        } else {
            this.emitCurrentData();
        }
        return this.items;
    }

    public async loadNextPage(): Promise<ITEM[]> {
        if (this.isAllLoaded) {
            return this.items;
        }
        const items = await this.fetchNextPage();
        this.items = this.items.concat(items);
        this.emitCurrentData();
        return this.items;
    }

    public async loadTotalSize(): Promise<number> {
        const {value: size} = await this.loadListSize(this.params);
        this.size = size;
        this.emitCurrentData();
        return this.size;
    }

    public replaceItem(updatedItem: ITEM) {
        this.items = replaceAll(this.items, byIdOf(updatedItem), updatedItem);
        this.emitCurrentData();
    }

    public async reloadAllLoaded(): Promise<ITEM[]> {
        const pages = range(FIRST_PAGE, this.getLastPageNumber());
        for (const page of pages) {
            await this.reloadPage(page); // emits
        }
        return this.items;
    }

    public resetAndReloadFirstPage(): Promise<ITEM[]> {
        this.reset(); // emits
        return this.loadFirstPage(); // emits
    }

    public async reloadPage(pageNumber: number): Promise<ITEM[]> {
        this.assertPageIsLoaded(pageNumber);
        const reloadedPage= await this.fetchPage(this.getRequestPage(pageNumber));
        const startIndex = this.#nextPage.pageSize * (pageNumber - 1); // pages use 1-base indexing (we need to subtract 1)
        this.items = this.items.toSpliced(startIndex, this.#nextPage.pageSize, ...reloadedPage);
        this.emitCurrentData();
        return reloadedPage;
    }

    public async reloadItem(toBeReloadedItem: ITEM): Promise<ITEM> {
        let reloadedItem: ITEM;
        if (exists(this.loadSingleItemFunction)) {
            reloadedItem = await this.loadSingleItemFunction(toBeReloadedItem.id);
        } else {
            const index = findFirstIndex(this.items, byIdOf(toBeReloadedItem));
            const pageNumber = Math.floor(index / this.#nextPage.pageSize) + 1; // pages use 1-base indexing (we need to add 1)
            const reloadedPage = await this.reloadPage(pageNumber);
            reloadedItem = findFirst(reloadedPage, byIdOf(toBeReloadedItem));
        }
        this.items = replaceAll(this.items, byIdOf(reloadedItem), reloadedItem);
        this.emitCurrentData();
        return reloadedItem;
    }

    public async removeItem(toBeRemoved: ITEM): Promise<void> {
        if (this.isAllLoaded) {
            this.items = this.items.filter((item) => item !== toBeRemoved);
            this.size--;
            this.emitCurrentData();
        } else {
            this.items = this.items.filter((item) => item !== toBeRemoved);
            this.emitCurrentData();
            const items = await this.fetchCurrentPage();
            const replacement = items.at(-1);
            this.items = [...this.items, replacement];
            await this.loadTotalSize(); // emits
        }
    }

    public removeItemById(id: ID): Promise<void> {
        const toBeRemoved = this.items.find((item) => item.id === id);
        return this.removeItem(toBeRemoved); // emits
    }

    public removeAll() {
        this.init();
        this.items = [];
        this.size = 0;
        this.emitCurrentData();
    }

    get isPartiallyLoaded(): boolean {
        return exists(this.items);
    }

    get isAllLoaded(): boolean {
        if (exists(this.items)) {
            return this.items.length >= this.size;
        } else {
            return this.size === 0;
        }
    }

    get loadedSize(): number | null {
        if (isNullOrUndefined((this.items))) {
            return null;
        }
        return this.items.length;
    }

    get totalSize(): number | null {
        return this.size;
    }

    get currentState(): State {
        return this.state;
    }

    get nextPage(): Readonly<Pageable> {
        return {...this.#nextPage}
    }

    get loadedItems(): ITEM[] | null {
        return this.isPartiallyLoaded ? [...this.items] : null;
    }

    get params(): PARAMS {
        return {...this.currentParams};
    }

    get errorMessage(): string | null {
        return this.loadNextPageErrorMessage;
    }

    protected emitCurrentData() {
        this.reactiveState$.next(this.createState())
    }

    protected getRequestPage(pageToLoad?: number): Pageable {
        const pageNumber = pageToLoad ?? this.#nextPage.pageNumber;
        this.assertPageNumberIsPositive(pageNumber);
        return {...this.#nextPage, pageNumber};
    }

    protected fetchPage(page: Pageable) {
        return this.loadListItems(page, this.params);
    }

    private createState(): PageableListState<ITEM, ID, PARAMS> {
        return {
            nextPage: this.nextPage,
            currentState: this.currentState,
            isAllLoaded: this.isAllLoaded,
            isPartiallyLoaded: this.isPartiallyLoaded,
            loadedItems: this.loadedItems,
            loadedSize: this.loadedSize,
            params: this.params,
            totalSize: this.totalSize,
            errorMessage: this.errorMessage
        }
    }

    private async fetchNextPage(): Promise<ITEM[]> {
        this.state = states.PROCESSING;
        this.loadNextPageErrorMessage = null;
        this.emitCurrentData();
        try {
            const pageToLoad = this.nextPage; // get copy to prevent race condition of double +1
            const items = await this.fetchPage(pageToLoad);
            this.state = states.NORMAL;
            this.#nextPage = this.getRequestPage(pageToLoad.pageNumber + 1);
            return items;
        } catch (error) {
            this.loadNextPageErrorMessage = error.text ?? JSON.stringify(error);
            this.state = states.ERRORED;
            throw error;
        } finally {
            this.emitCurrentData();
        }
    }

    private fetchCurrentPage(): Promise<ITEM[]> {
        const page = this.getRequestPage(this.getLastPageNumber());
        return this.fetchPage(page);
    }

    private getLastPageNumber(): number {
        return Math.max(FIRST_PAGE, this.#nextPage.pageNumber - 1); // should not go lower than 1
    }

    private assertPageNumberIsPositive(pageNumber: number): void {
        assertExists(pageNumber);
        if (pageNumber <= 0) {
            throw new Error(`pageNumber have to be greater than 0, but it is ${pageNumber}!`);
        }
    }

    private assertPageIsLoaded(pageNumber: number): void {
        assertExists(pageNumber);
        if (pageNumber >= this.nextPage.pageNumber) {
            throw new Error(`Page with pageNumber ${pageNumber} is not yet loaded!`);
        }
    }

    private init() {
        this.#nextPage = {pageNumber: FIRST_PAGE, pageSize: this.pageSize};
        this.items = null;
        this.size = null;
        this.state = states.INITIAL;
        this.currentParams = this.initialParams;
        this.loadNextPageErrorMessage = null;
    }
}

